/**
 * Utility functions for converting between pixel coordinates and timestamps
 * in the trending chart view
 */

export interface ChartArea {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

export interface TimeRange {
	start: number; // timestamp in milliseconds
	end: number; // timestamp in milliseconds
}

/**
 * Convert a pixel X coordinate to a timestamp based on the current chart area and visible time range
 * Uses linear interpolation to map pixel position to time
 *
 * @param pixelX - The X coordinate in pixels
 * @param chartArea - The chart's pixel boundaries
 * @param timeRange - The visible time range in milliseconds
 * @returns The corresponding timestamp in milliseconds
 */
export function pixelToTimestamp(
	pixelX: number,
	chartArea: ChartArea,
	timeRange: TimeRange,
): number {
	// Clamp pixel to chart boundaries
	const clampedPixel = Math.max(
		chartArea.left,
		Math.min(pixelX, chartArea.right),
	);

	// Calculate the pixel range and time range
	const pixelRange = chartArea.right - chartArea.left;
	const timeSpan = timeRange.end - timeRange.start;

	// Handle edge case: zero pixel range
	if (pixelRange <= 0) {
		return timeRange.start;
	}

	// Calculate progress through the chart (0 to 1)
	const pixelOffset = clampedPixel - chartArea.left;
	const progress = pixelOffset / pixelRange;

	// Convert progress to timestamp
	const timestamp = timeRange.start + progress * timeSpan;

	return Math.round(timestamp);
}

/**
 * Convert a timestamp to a pixel X coordinate based on the current chart area and visible time range
 * Uses linear interpolation to map time to pixel position
 *
 * @param timestamp - The timestamp in milliseconds
 * @param chartArea - The chart's pixel boundaries
 * @param timeRange - The visible time range in milliseconds
 * @returns The corresponding X coordinate in pixels
 */
export function timestampToPixel(
	timestamp: number,
	chartArea: ChartArea,
	timeRange: TimeRange,
): number {
	// Calculate the time span and pixel range
	const timeSpan = timeRange.end - timeRange.start;
	const pixelRange = chartArea.right - chartArea.left;

	// Handle edge case: zero time span
	if (timeSpan <= 0) {
		return chartArea.left;
	}

	// Calculate progress through time (0 to 1)
	const timeOffset = timestamp - timeRange.start;
	const progress = timeOffset / timeSpan;

	// Clamp progress to 0-1 range (in case timestamp is outside range)
	const clampedProgress = Math.max(0, Math.min(progress, 1));

	// Convert progress to pixel
	const pixelX = chartArea.left + clampedProgress * pixelRange;

	return pixelX;
}

/**
 * Initialize cursor positions at relative positions (e.g., 10% and 90%) of the time range
 *
 * @param timeRange - The visible time range in milliseconds
 * @param percent1 - Position of first cursor as percentage (0-1)
 * @param percent2 - Position of second cursor as percentage (0-1)
 * @returns Tuple of [cursor1Timestamp, cursor2Timestamp]
 */
export function initializeCursorTimestamps(
	timeRange: TimeRange,
	percent1: number = 0.1,
	percent2: number = 0.9,
): [number, number] {
	const timeSpan = timeRange.end - timeRange.start;

	const cursor1Time = timeRange.start + timeSpan * percent1;
	const cursor2Time = timeRange.start + timeSpan * percent2;

	return [Math.round(cursor1Time), Math.round(cursor2Time)];
}
